/**
 * Phase 1.7 — single EmptyState primitive.
 *
 * Replaces the v1 EmptyState. One component, three slots:
 *   - icon          required (lucide or inline svg)
 *   - title         required
 *   - description   optional
 *   - action        optional CTA, usually a v2 Button
 *
 * Density variants:
 *   - 'card'   embedded inside a Card body — small, no border, no bg
 *   - 'page'   full-page placeholder — dashed border, sand bg, generous pad
 *
 * The audit found 6 different empty-state treatments scattered across
 * Phase 1.5/1.6 modules. This collapses them.
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  variant?: 'card' | 'page';
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = 'page',
  className,
}: EmptyStateProps): JSX.Element {
  const card = variant === 'card';
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        card
          ? 'py-8'
          : 'rounded-xl border border-dashed border-border bg-sand py-16 px-8 dark:bg-surface-raised-dark',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full',
          card
            ? 'h-10 w-10 bg-surface-raised text-muted-foreground dark:bg-surface-raised-dark'
            : 'h-14 w-14 bg-surface text-clay shadow-sm dark:bg-surface-dark dark:text-clay-dark',
        )}
      >
        <Icon className={card ? 'h-5 w-5' : 'h-6 w-6'} />
      </div>
      <h3
        className={cn(
          'mt-4 font-display text-charcoal dark:text-charcoal-dark',
          card ? 'text-base' : 'text-xl',
        )}
      >
        {title}
      </h3>
      {description ? (
        <p
          className={cn(
            'mt-1 text-sm text-muted-foreground',
            card ? 'max-w-xs' : 'max-w-md',
          )}
        >
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
